import { encryptStorage } from '../utils/encryptStorage';

const PENDING_CONTACTS_KEY = 'pending_recharge_contacts_v2';
const PENDING_PRODUCT_KEY = 'pending_recharge_product_v2';
const TTL_MS = 30 * 60 * 1000; // 30 minutos

/**
 * Codifica rechargeable_id y rechargeable_type en base64 para pasar por URL/estado de navegación.
 * Solo incluye datos no sensibles (IDs de producto).
 */
export function encodePr(rechargeable_id, rechargeable_type) {
  try {
    return btoa(JSON.stringify({ i: rechargeable_id, t: rechargeable_type }));
  } catch {
    return null;
  }
}

/**
 * Decodifica el parámetro `pr` de vuelta a { rechargeable_id, rechargeable_type }.
 * Retorna null si el valor es inválido.
 */
export function decodePr(pr) {
  try {
    const decoded = JSON.parse(atob(pr));
    if (!decoded?.i || !decoded?.t) return null;
    return { rechargeable_id: decoded.i, rechargeable_type: decoded.t };
  } catch {
    return null;
  }
}

/**
 * Hook para gestionar contactos de recarga pendiente con TTL.
 * Los contactos (datos sensibles) se almacenan en encryptedStorage con expiración de 30 minutos.
 */
export default function usePendingRecharge() {
  const savePendingContacts = (contacts) => {
    if (!contacts?.length) return;
    encryptStorage.setItem(PENDING_CONTACTS_KEY, {
      contacts,
      expires_at: Date.now() + TTL_MS,
    });
  };

  const getPendingContacts = () => {
    try {
      const data = encryptStorage.getItem(PENDING_CONTACTS_KEY);
      if (!data) return null;
      if (Date.now() > data.expires_at) {
        encryptStorage.removeItem(PENDING_CONTACTS_KEY);
        return null;
      }
      return data.contacts ?? null;
    } catch {
      return null;
    }
  };

  const clearPendingContacts = () => {
    encryptStorage.removeItem(PENDING_CONTACTS_KEY);
  };

  /**
   * Guarda el producto pendiente (rechargeable_id + rechargeable_type codificados como pr).
   * Usado desde Item.jsx (SpecialOffers/Offers) cuando el usuario no está autenticado.
   */
  const savePendingProduct = (pr) => {
    if (!pr) return;
    encryptStorage.setItem(PENDING_PRODUCT_KEY, {
      pr,
      expires_at: Date.now() + TTL_MS,
    });
  };

  const getPendingProduct = () => {
    try {
      const data = encryptStorage.getItem(PENDING_PRODUCT_KEY);
      if (!data) return null;
      if (Date.now() > data.expires_at) {
        encryptStorage.removeItem(PENDING_PRODUCT_KEY);
        return null;
      }
      return data.pr ?? null;
    } catch {
      return null;
    }
  };

  const clearPendingProduct = () => {
    encryptStorage.removeItem(PENDING_PRODUCT_KEY);
  };

  return {
    savePendingContacts, getPendingContacts, clearPendingContacts,
    savePendingProduct, getPendingProduct, clearPendingProduct,
  };
}
